import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Globe, MapPin, ChevronDown, Lock, Rocket } from "lucide-react";
import { SITE, NAV_LINKS, LEGAL_LINKS, REGIONS } from "@/data/content";
import { legalBusinessIdentity } from "@/data/legalBusinessIdentity";
import { CONCEPT_CATEGORIES } from "@/data/conceptCategories";
import { useRegion } from "@/hooks/useRegion";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { fetchPublicBusinessSlugs } from "@/lib/api";

function CategoryAccordion({ category, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={`footer-category-${category.key}`} className="border-b border-white/10 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`footer-category-toggle-${category.key}`}
        aria-expanded={open}
        className="w-full flex items-center justify-between py-3 text-left"
      >
        <span className="text-sm font-semibold uppercase tracking-wide text-[#10B981]">
          {category.label} <span className="text-slate-500 font-normal">({category.items.length})</span>
        </span>
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="flex flex-wrap gap-3 pb-4" data-testid={`footer-category-list-${category.key}`}>
          {category.items.length > 0 ? (
            category.items.map((it) => (
              <a
                key={it.slug}
                href={it.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`footer-website-${it.slug}`}
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm font-medium text-slate-200 hover:border-[#10B981] hover:text-white transition-colors"
              >
                <span className="h-2 w-2 rounded-full" style={{ background: it.color }} />
                {it.label}
                <span className="text-[10px] uppercase tracking-wide text-slate-500">{it.badge}</span>
              </a>
            ))
          ) : (
            <p className="text-sm italic text-slate-500" data-testid={`footer-category-empty-${category.key}`}>
              {category.emptyLabel}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function Footer() {
  const { region, link } = useRegion();
  const { revealAdminUi } = useAdminAuth();
  const site = REGIONS[region];
  const [publicSlugs, setPublicSlugs] = useState(new Set());

  useEffect(() => {
    fetchPublicBusinessSlugs()
      .then((slugs) => setPublicSlugs(new Set(slugs)))
      .catch(() => {});
  }, []);

  const managedCategories = CONCEPT_CATEGORIES.map((cat) => ({
    ...cat,
    items: cat.items.filter((it) => publicSlugs.has(it.slug)),
  }));
  const hasAnyManaged = managedCategories.some((cat) => cat.items.length > 0);

  return (
    <footer data-testid="site-footer" className="bg-[#0F172A] text-slate-300 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <img src="/images/logo2.png" alt="MySiteCare" className="h-12 w-auto object-contain mb-4 rounded-lg" />
            <p className="text-sm leading-relaxed text-slate-400">
              {SITE.legalName}{region === "ph" ? " — Philippines" : ""}. MySiteCare builds and takes over business
              websites, with hosting, maintenance, monitoring, backups, and technical responsibility available
              according to the selected plan.
            </p>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 font-display">Navigation</h4>
            <ul className="space-y-2.5">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={link(l.to)}
                    data-testid={`footer-nav-${l.label.toLowerCase()}`}
                    className="text-sm text-slate-400 hover:text-[#10B981] transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 font-display">Legal</h4>
            <ul className="space-y-2.5">
              {LEGAL_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={link(l.to)}
                    data-testid={`footer-legal-${l.to.replace("/", "")}`}
                    className="text-sm text-slate-400 hover:text-[#10B981] transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 font-display">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <Mail size={17} className="text-[#10B981] mt-0.5 shrink-0" />
                <a href={`mailto:${SITE.email}`} data-testid="footer-email" className="text-slate-400 hover:text-white transition-colors break-all">
                  {SITE.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={17} className="text-[#10B981] mt-0.5 shrink-0" />
                <a href={`tel:${SITE.phoneHref}`} data-testid="footer-phone" className="text-slate-400 hover:text-white transition-colors">
                  {SITE.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Globe size={17} className="text-[#10B981] mt-0.5 shrink-0" />
                <a href={site.website} target="_blank" rel="noopener noreferrer" data-testid="footer-website" className="text-slate-400 hover:text-white transition-colors">
                  {site.website.replace("https://", "")}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={17} className="text-[#10B981] mt-0.5 shrink-0" />
                <span data-testid="footer-service-area" className="text-slate-400">
                  Service area: {SITE.serviceArea}
                </span>
              </li>
              <li data-testid="footer-business-identity" className="border-t border-white/10 pt-3 text-xs leading-relaxed text-slate-400">
                <span className="font-semibold text-slate-200">{legalBusinessIdentity.formalBusinessName}</span><br />
                Public brand: MySiteCare · Philippine sole proprietorship · DTI Business Name No. {legalBusinessIdentity.dti.businessNameNumber}
                <br />
                Registered / Business Address: {legalBusinessIdentity.registeredBusinessAddress}
              </li>
            </ul>
          </div>
        </div>

        {region === "ph" && (
        <div data-testid="footer-ph-managed-section" className="mt-12 pt-8 border-t border-white/10">
          <h4 className="text-white font-semibold mb-1 font-display flex items-center gap-2"><Rocket size={16} className="text-[#10B981]" /> MySiteCare Managed Websites</h4>
          <p className="text-xs text-slate-500 mb-4">Businesses now live and managed by MySiteCare, on a plan or after purchase — their concept site is now their real, working website.</p>
          {hasAnyManaged ? (
            <div>
              {managedCategories.filter((cat) => cat.items.length > 0).map((cat) => (
                <CategoryAccordion key={cat.key} category={cat} defaultOpen={false} />
              ))}
            </div>
          ) : (
            <p className="text-sm italic text-slate-500" data-testid="footer-managed-empty">No businesses are live yet — this section fills in as concept sites are taken over or purchased.</p>
          )}
        </div>
        )}

        {region === "ph" && revealAdminUi && (
        <div data-testid="footer-ph-concepts-section" className="mt-12 pt-8 border-t border-white/10">
          <h4 className="text-white font-semibold mb-1 font-display">Concept Websites / Demonstrations <span className="text-[10px] font-normal uppercase tracking-wide text-slate-500">(private — admin only)</span></h4>
          <p className="text-xs text-slate-500 mb-4">Philippines businesses only — shown here on the Philippines site. Click a category to expand.</p>
          <div>
            {CONCEPT_CATEGORIES.map((cat) => (
              <CategoryAccordion key={cat.key} category={cat} defaultOpen={false} />
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-500">Unsolicited concept sites built by MySiteCare. Not affiliated with the businesses shown unless stated.</p>
        </div>
        )}
        {region === "ph" && !revealAdminUi && (
          <div className="mt-8 pt-6 border-t border-white/10">
            <Link to="/admin/login" data-testid="footer-admin-login-link" className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-400 transition-colors">
              <Lock size={12} /> Admin login
            </Link>
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-white/10">
          <p data-testid="footer-operator" className="text-sm text-slate-400 mb-4">
            This site is operated by <span className="text-white font-medium">{SITE.legalName}</span>. Official operator
            website:{" "}
            <a
              href="https://mysitecare.eu"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#10B981] hover:underline"
            >
              https://mysitecare.eu
            </a>
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500" data-testid="footer-copyright">
              © {SITE.year} {SITE.legalName}. Owned and operated by {legalBusinessIdentity.proprietor}. DTI
              Business Name No. {legalBusinessIdentity.dti.businessNameNumber}. All rights reserved.
            </p>
            <p className="text-xs text-slate-600">
              {region === "ph"
                ? "Website responsibility services — Philippines"
                : "Website responsibility services — EU and international"}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
